import React, { Dispatch, useCallback, useMemo, useState } from 'react';

import {
  BUSINESS_UNIT,
  PLANNING_STAGE,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import {
  trackOnboardingStarted,
  trackOnboardingCompleted,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';

import { AuthModal } from './AuthModal';
import { ModalV2Props } from '../Modal/ModalV2';
import { QuickSignupFlow } from './QuickSignupFlow';
import { SaveProgressModal, SaveProgressModalTypes } from './SaveProgressModal';
import { SimpleSignupModal } from './SimpleSignupModal';

import { CARD_TYPE_MAP, LeadCardType } from '../../paper/cards/constants/cardTypes';
import { getCardPlpPath } from '../../paper/cards/util/getCardUrl';
import {
  mapCardTypeToBusinessCategory,
  mapCardTypeToBusinessUnitComponent,
} from '../../paper/cards/util/trackingHelpers';
import { PaperOnboardTypes } from './constants';
import { transferDraftsToExistingAccount } from './helpers';
import { usePaperOnboardContext } from './PaperOnboardContext';

const FLOW_ENTRY = 'CUSTOMIZATION_SAVE';

export const onOnboardingStarted = ({
  businessUnitComponent,
  isGuest,
  onboardingStartedTracked,
  setOnboardingStartedTracked,
}: {
  businessUnitComponent: string;
  isGuest: boolean;
  onboardingStartedTracked: boolean;
  setOnboardingStartedTracked: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  if (!onboardingStartedTracked) {
    trackOnboardingStarted(BUSINESS_UNIT.INVITATIONS, businessUnitComponent, !isGuest);
    setOnboardingStartedTracked(true);
  }
};

export const onOnboardSuccessCb = async ({
  businessUnitComponent,
  closeOnboardPrompt,
  fetchUserContext,
  paperOnboardCallback,
  redirectHref,
}: {
  businessUnitComponent: string;
  closeOnboardPrompt: () => void;
  fetchUserContext: () => Promise<UserContext>;
  paperOnboardCallback?: () => void;
  redirectHref?: string;
}) =>
  fetchUserContext()
    .then((freshUserContext) => {
      closeOnboardPrompt();

      trackOnboardingCompleted(BUSINESS_UNIT.INVITATIONS, businessUnitComponent, {
        isExistingUser: !freshUserContext.is_guest,
        weddingDate: freshUserContext.user_role_account_weddings?.[0].wedding_date,
      });

      if (paperOnboardCallback) {
        paperOnboardCallback();
      }

      if (redirectHref) {
        window.location.assign(redirectHref);
      }
    })
    .catch(() => undefined);

export const onLoginSuccess = async ({
  businessUnitComponent,
  closeOnboardPrompt,
  fetchUserContext,
  paperOnboardCallback,
  redirectHref,
  userContext,
}: {
  businessUnitComponent: string;
  closeOnboardPrompt: () => void;
  fetchUserContext: () => Promise<UserContext>;
  paperOnboardCallback?: () => void;
  redirectHref?: string;
  userContext: UserContext;
}) => {
  await transferDraftsToExistingAccount(userContext.object_id || '');
  await onOnboardSuccessCb({
    businessUnitComponent,
    closeOnboardPrompt,
    fetchUserContext,
    paperOnboardCallback,
    redirectHref,
  });
};

type PaperOnboardContainerProps = {
  appDispatch: Dispatch<unknown>;
  fetchUserContext: () => Promise<UserContext>;
} & Pick<ModalV2Props, 'rootId'>;

/**
 * Contains base paper onboard flows that is shared between paper customization, cart, and checkout
 */
export const PaperOnboardContainer: React.FC<PaperOnboardContainerProps> = ({
  appDispatch,
  fetchUserContext,
  rootId,
}): JSX.Element => {
  const {
    isPushSignupIntoCustomization,
    openPaperOnboard,
    paperOnboardCallback,
    paperOnboardType,
    projectType = '',
    redirectHref,
    userContext,
  } = usePaperOnboardContext();

  const businessCategory = useMemo(() => mapCardTypeToBusinessCategory(projectType), [projectType]);
  const businessUnitComponent = useMemo(
    () => mapCardTypeToBusinessUnitComponent(projectType),
    [projectType]
  );
  const formInitialValues = useMemo(
    () => ({
      wedding_planning_stage:
        projectType === CARD_TYPE_MAP.std
          ? PLANNING_STAGE.PLANNING_NO_VENUE_BOOKED
          : PLANNING_STAGE.PLANNING_VENUE_BOOKED,
    }),
    [projectType]
  );
  const isGuest = useMemo(() => Boolean(userContext?.is_guest), [userContext]);

  const [onboardingStartedTracked, setOnboardingStartedTracked] = useState(false);

  const closeOnboardPrompt = useCallback(() => {
    openPaperOnboard({ paperOnboardType: undefined });
  }, [openPaperOnboard]);

  if (!isPushSignupIntoCustomization || !isGuest) return <></>;

  return (
    <>
      {paperOnboardType === PaperOnboardTypes.AUTH && (
        <AuthModal
          onClose={closeOnboardPrompt}
          loginCallback={() => {
            onLoginSuccess({
              businessUnitComponent,
              closeOnboardPrompt,
              fetchUserContext,
              paperOnboardCallback,
              redirectHref,
              userContext,
            }).catch(() => undefined);
          }}
          rootId={rootId}
          signupLinkCallback={() => {
            openPaperOnboard({
              paperOnboardCallback,
              paperOnboardType: PaperOnboardTypes.SIMPLE_SIGNUP,
              projectType,
              redirectHref,
            });
            return false;
          }}
        />
      )}
      {(paperOnboardType === PaperOnboardTypes.QUICK_SIGNUP ||
        paperOnboardType === PaperOnboardTypes.QUICK_SIGNUP_CHECKOUT ||
        paperOnboardType === PaperOnboardTypes.QUICK_SIGNUP_GUEST_LIST) && (
        <QuickSignupFlow
          appDispatch={appDispatch}
          businessCategory={businessCategory}
          businessUnitComponent={businessUnitComponent}
          flowEntry={FLOW_ENTRY}
          formInitialValues={formInitialValues}
          onClose={closeOnboardPrompt}
          onLoginSuccess={() =>
            onLoginSuccess({
              businessUnitComponent,
              closeOnboardPrompt,
              fetchUserContext,
              paperOnboardCallback,
              redirectHref,
              userContext,
            })
          }
          onOnboardingStarted={() =>
            onOnboardingStarted({
              businessUnitComponent,
              isGuest,
              onboardingStartedTracked,
              setOnboardingStartedTracked,
            })
          }
          onOnboardSuccessCb={() =>
            onOnboardSuccessCb({
              businessUnitComponent,
              closeOnboardPrompt,
              fetchUserContext,
              paperOnboardCallback,
              redirectHref,
            })
          }
          rootId={rootId}
          userContext={userContext}
        />
      )}
      {paperOnboardType === PaperOnboardTypes.SAVE_PROGRESS && (
        <SaveProgressModal
          onClose={closeOnboardPrompt}
          onClickPrimaryCta={() => {
            openPaperOnboard({
              paperOnboardCallback,
              paperOnboardType: PaperOnboardTypes.QUICK_SIGNUP,
              projectType,
              redirectHref,
            });
          }}
          onClickSecondaryCta={() => {
            window.location.assign(getCardPlpPath(projectType as LeadCardType));
          }}
          rootId={rootId}
        />
      )}
      {paperOnboardType === PaperOnboardTypes.SAVE_PROGRESS_REMOVE && (
        <SaveProgressModal
          onClose={closeOnboardPrompt}
          onClickPrimaryCta={() => {
            openPaperOnboard({
              paperOnboardCallback,
              paperOnboardType: PaperOnboardTypes.QUICK_SIGNUP,
              projectType,
              redirectHref,
            });
          }}
          onClickSecondaryCta={() => {
            if (paperOnboardCallback) {
              paperOnboardCallback();
            }
            closeOnboardPrompt();
          }}
          modalType={SaveProgressModalTypes.REMOVE}
          rootId={rootId}
        />
      )}
      {paperOnboardType === PaperOnboardTypes.SIMPLE_SIGNUP && (
        <SimpleSignupModal
          cardType={projectType}
          closeModal={closeOnboardPrompt}
          flowEntry={FLOW_ENTRY}
          fetchUserContext={fetchUserContext}
          isPushSignupIntoCustomization={isPushSignupIntoCustomization}
          loginCallback={() =>
            onLoginSuccess({
              businessUnitComponent,
              closeOnboardPrompt,
              fetchUserContext,
              paperOnboardCallback,
              redirectHref,
              userContext,
            })
          }
          loginLinkCallback={() => {
            openPaperOnboard({
              paperOnboardCallback,
              paperOnboardType: PaperOnboardTypes.AUTH,
              projectType,
              redirectHref,
            });
          }}
          onClose={closeOnboardPrompt}
          rootId={rootId}
          shouldShowPartnerField={false}
          userContext={userContext}
        />
      )}
    </>
  );
};
